import { useContext, useEffect } from 'react'
import { Font, Template } from '../../../types'
import BeefreeSDK from '@beefree.io/sdk'
import api from '../../../api'
import { ProjectContext } from '../../../contexts'
import { Editor } from './EmailEditor'
import { PreferencesContext } from '../../../ui/PreferencesContext'
import { fontWeightMap } from './fonts'

const blankTemplate = {
    page: {
        body: {
            container: { style: { 'background-color': '#FFFFFF' } },
            content: {
                computedStyle: { messageWidth: '600px' },
                style: { 'font-family': 'Arial, Helvetica, sans-serif', color: '#000000' },
            },
            type: 'mailup-bee-page-properties',
        },
        rows: [{
            type: 'one-column-empty',
            container: {},
            content: {
                style: {
                    'background-color': 'transparent',
                    color: '#000000',
                    width: '600px',
                    'padding-top': '0px',
                    'padding-bottom': '0px',
                    'padding-right': '0px',
                    'padding-left': '0px',
                },
            },
            columns: [{
                'grid-columns': 12,
                modules: [],
                style: {
                    'padding-top': '0px',
                    'padding-bottom': '0px',
                    'padding-left': '0px',
                    'padding-right': '0px',
                },
            }],
        }],
        template: { name: 'template-base', type: 'basic', version: '0.0.1' },
    },
}

interface BeefreeEditorProps {
    template: Template
    setEditor: (editor: Editor | undefined) => void
    setTemplate: (template: Template) => void
}

export default function BeefreeEditor({ template, setEditor }: BeefreeEditorProps) {

    const [preferences] = useContext(PreferencesContext)
    const [project] = useContext(ProjectContext)

    const languages: Record<string, any> = {
        en: 'en-US',
        es: 'es-ES',
        pt: 'pt-BR',
    }

    useEffect(() => {
        async function initializeEditor() {
            const token = await api.templates.editor(project.id)

            const fontResources = await api.resources.all(project.id, 'font').catch(() => [])
            const customFonts = fontResources
                .map(resource => resource.value as Font)
                .filter(font => font.url)
                .map(font => ({
                    name: font.name,
                    fontFamily: font.value,
                    url: font.url,
                    ...(font.weights?.length ? { fontWeight: fontWeightMap(font.weights) } : {}),
                }))

            // Initialize the editor
            const bee = new BeefreeSDK(token)
            await bee.start({
                container: 'beefree-editor',
                language: languages[preferences.lang] ?? 'en-US',
                editorFonts: {
                    showDefaultFonts: true,
                    customFonts,
                },
                mergeTags: [{
                    name: 'First Name',
                    value: '{{user.firstName}}',
                    previewValue: 'John',
                }, {
                    name: 'Last Name',
                    value: '{{user.lastName}}',
                    previewValue: 'Smith',
                }, {
                    name: 'Full Name',
                    value: '{{user.fullName}}',
                    previewValue: 'John Smith',
                }],
                specialLinks: [{
                    type: 'Subscription',
                    label: 'Unsubscribe Link',
                    link: '{{unsubscribeEmailUrl}}',
                },
                {
                    type: 'Subscription',
                    label: 'Preference Center URL',
                    link: '{{preferencesUrl}}',
                }],
                contentDefaults: {
                    row: {
                        styles: {
                            paddingTop: '0px',
                            paddingBottom: '0px',
                        },
                    },
                    paragraph: {
                        blockOptions: {
                            paddingLeft: '20px',
                            paddingRight: '20px',
                        },
                    },
                },
                advancedPermissions: {
                    components: {
                        filePicker: {
                            canApplyEffects: true,
                            canChangeImage: true,
                            canChangeVideo: false,
                            canCreateFolder: false, // Disabled end user's ability to create a folder
                            canDeleteFile: true,
                            canDeleteFolder: false,
                            canImportFile: true,
                            canSearchFreePhotos: false,
                            canSearchFreeVideos: false,
                            canUpload: true,
                            maxUploadFileSize: 10485760,
                        },
                    },
                },
            }, template.data.json ?? blankTemplate)

            setEditor({
                save: async () => {
                    const result = await bee.save()
                    template.data.json = result.data.json
                    template.data.html = result.data.html
                    return template
                },
            })
        }

        initializeEditor().catch(console.error)

        return () => {
            setEditor(undefined)
        }
    }, [template.id])

    return (
        <div
            id="beefree-editor"
            style={{
                height: 'calc(100vh - 60px)',
                width: '100%',
            }}
        />
    )
}
